/**
 * Display formatting for report metric values.
 *
 * The reports API sends every metric as a plain JSON number (money rounded to
 * 2 dp, counts as integers, percents on a 0–100 scale) or `null` when the value
 * is unavailable (e.g. "not tracked yet", or a percent whose denominator is 0).
 * These helpers turn those numbers into display strings; `null` always renders
 * as an em dash so an unavailable metric never masquerades as a real zero.
 */
import { formatCurrency } from "./cashDrawerUtils";

export type MetricFormat = "money" | "count" | "percent";

const EMPTY = "—";

const COUNT_FORMATTER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const isMissing = (value: number | null | undefined): value is null =>
  value === null || value === undefined || Number.isNaN(value);

// `+ 0` turns -0 into 0: a small negative that rounds to zero (e.g. -0.4)
// would otherwise render as "-0".
const withoutNegativeZero = (n: number): number => n + 0;

/** Integer count with thousands separators: 1204 → "1,204". */
export function formatCount(value: number | null | undefined): string {
  if (isMissing(value)) return EMPTY;
  return COUNT_FORMATTER.format(
    withoutNegativeZero(Math.round(value as number)),
  );
}

/**
 * Percent from a 0–100 number (the API's scale, NOT a 0–1 ratio):
 * 42.857 → "42.9%", 50 → "50%".
 */
export function formatPercent(value: number | null | undefined): string {
  if (isMissing(value)) return EMPTY;
  // Round to the formatter's 1 decimal first so -0.04 shows "0%", not "-0%".
  const rounded = Math.round((value as number) * 10) / 10;
  return `${PERCENT_FORMATTER.format(withoutNegativeZero(rounded))}%`;
}

/** Route a metric value to the formatter for its declared `format`. */
export function formatMetricValue(
  value: number | null | undefined,
  format: MetricFormat,
): string {
  switch (format) {
    case "money":
      return formatCurrency(value);
    case "percent":
      return formatPercent(value);
    case "count":
    default:
      return formatCount(value);
  }
}
